import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { COMPANY } from "@/config/company";

export default function PrintLabelPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [it, setIt] = useState(null);

  useEffect(() => {
    api.get(`/interventions/${id}`).then(({ data }) => setIt(data)).catch(() => nav("/"));
  }, [id, nav]);

  useEffect(() => {
    if (it) setTimeout(() => window.print(), 400);
  }, [it]);

  if (!it) return <div className="p-10">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-secondary/40 p-6">
      <style>{`
        @page { size: 152mm 102mm; margin: 0; }
        @media print {
          body * { visibility: hidden; }
          .print-label, .print-label * { visibility: visible; }
          .print-label {
            position: fixed; left: 0; top: 0;
            width: 152mm; height: 102mm; padding: 6mm;
            background: #fff; color: #000; box-sizing: border-box;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto no-print mb-6 flex justify-between items-center">
        <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                style={{ transition: "color 0.15s ease" }}>
          <ArrowLeft size={14} /> Indietro
        </button>
        <Button onClick={() => window.print()} data-testid="print-now-btn" className="rounded-full gap-2">
          <Printer size={16} weight="bold" /> Stampa (100×150 mm)
        </Button>
      </div>

      {/* Etichetta 4x6" orizzontale */}
      <div
        className="print-label mx-auto bg-white border-2 border-black text-black relative"
        style={{ width: "152mm", height: "102mm", padding: "6mm", boxSizing: "border-box" }}
        data-testid="label-container"
      >
        {/* Header: logo + brand */}
        <div className="flex items-center gap-4 border-b-2 border-black pb-2">
          {COMPANY.logoUrl && (
            <img src={COMPANY.logoUrl} alt="Logo" style={{ height: "18mm", width: "auto" }} crossOrigin="anonymous" />
          )}
          <div className="flex-1">
            <div className="font-bold" style={{ fontSize: "9mm", lineHeight: 1, letterSpacing: "0.5mm" }}>
              {COMPANY.name.toUpperCase()}
            </div>
            <div style={{ fontSize: "3mm", marginTop: "1mm" }}>{COMPANY.tagline}</div>
            <div className="font-mono" style={{ fontSize: "2.5mm", marginTop: "1mm" }}>
              Tel: {COMPANY.phone} · {COMPANY.email}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono uppercase" style={{ fontSize: "2.5mm", letterSpacing: "0.5mm" }}>Data ingresso</div>
            <div className="font-bold" style={{ fontSize: "4mm", marginTop: "0.5mm" }}>
              {new Date(it.created_at).toLocaleDateString("it-IT")}
            </div>
            <div style={{ fontSize: "3mm" }}>
              ore {new Date(it.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>

        {/* Body: info + QR affiancati */}
        <div className="grid grid-cols-[1fr_auto] gap-6 mt-3" style={{ height: "62mm" }}>
          <div className="space-y-2 overflow-hidden">
            <div>
              <div className="font-mono uppercase" style={{ fontSize: "2.5mm", letterSpacing: "0.5mm" }}>Codice</div>
              <div className="font-bold font-mono" style={{ fontSize: "7mm", letterSpacing: "0.5mm", lineHeight: 1 }} data-testid="label-code">
                {it.code}
              </div>
            </div>

            <div>
              <div className="font-mono uppercase" style={{ fontSize: "2.5mm", letterSpacing: "0.5mm" }}>Cliente</div>
              <div className="font-bold" style={{ fontSize: "5.5mm", lineHeight: 1.1 }}>
                {it.customer_surname} {it.customer_name}
              </div>
              <div style={{ fontSize: "3.5mm" }}>Tel: {it.customer_phone}</div>
            </div>

            <div>
              <div className="font-mono uppercase" style={{ fontSize: "2.5mm", letterSpacing: "0.5mm" }}>Prodotto</div>
              <div style={{ fontSize: "4mm", lineHeight: 1.15 }}>
                {[it.product_type, it.product_brand, it.product_model].filter(Boolean).join(" — ")}
              </div>
              {it.serial_number && <div className="font-mono" style={{ fontSize: "3mm" }}>S/N: {it.serial_number}</div>}
            </div>

            <div>
              <div className="font-mono uppercase" style={{ fontSize: "2.5mm", letterSpacing: "0.5mm" }}>Lavoro</div>
              <div style={{ fontSize: "3.2mm", lineHeight: 1.2 }}>
                {it.work_description.length > 160 ? it.work_description.slice(0, 160) + "…" : it.work_description}
              </div>
            </div>
          </div>

          {/* QR */}
          <div className="text-center flex flex-col items-center justify-start">
            <div className="bg-white border-2 border-black" style={{ padding: "1.5mm" }}>
              <QRCodeSVG value={it.code} size={210} level="H" />
            </div>
            <div className="font-mono" style={{ fontSize: "2.8mm", marginTop: "1mm", letterSpacing: "0.3mm" }}>{it.code}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
